import React from 'react';

import { constVoid, option, string, tagged } from '@code-expert/prelude';
import type { UserPublic } from '/imports/domain';
import { isLTIUser, ltiErrorAdt } from '/imports/domain';
import { Logger } from '/imports/modules/Logger/client';
import { ltiErrorLink } from '/imports/startup/shared/routes/lti';
import { useGlobalContext } from '/imports/ui/components/GlobalContext';

type LtiVisitResult =
  | tagged.Tagged<'ok'>
  | tagged.Tagged<
      'redirect',
      { location: URL; cause: string; details: { ltiHost: string; referrer: string } }
    >;

const action = tagged.build<LtiVisitResult>();

export function RedirectNonLtiUsers() {
  const result = useLtiVisitResult();

  React.useEffect(() => {
    action.fold(result, {
      ok: constVoid,
      redirect: ({ location, cause, details }) => {
        Logger.persistAlways.warn(cause, details);
        window.location.replace(location);
      },
    });
  });

  return null;
}

const useLtiVisitResult = () => {
  const { user, ltiUrl, rootUrl: defaultHost } = useGlobalContext();
  const isLtiUser = (user: UserPublic | undefined) => user != null && isLTIUser(user);
  const isNormalUser = (user: UserPublic | undefined) => user != null && !isLTIUser(user);
  const currentLocation = new URL(window.location.href);
  const currentPathAndSearch = `${currentLocation.pathname}${currentLocation.search}`;
  const referrer = document.referrer;

  if (option.isSome(ltiUrl)) {
    const ltiHost = ltiUrl.value;
    const details = { ltiHost, referrer };
    const isLtiHost = (url: URL) => string.Eq.equals(url.origin, ltiHost);
    const isIFrame = window.self !== window.top;

    // redirect non LTI users to default URL
    if (isNormalUser(user) && isLtiHost(currentLocation)) {
      return action.redirect({
        location: new URL(currentPathAndSearch, defaultHost),
        cause: 'Non-LTI user tried to access LTI site',
        details,
      });
    }

    // redirect LTI users to LTI URL
    if (isLtiUser(user) && !isLtiHost(currentLocation)) {
      return action.redirect({
        location: new URL(currentPathAndSearch, ltiHost),
        cause: 'LTI user tried to access non-LTI site',
        details,
      });
    }

    // If we're running in an iFrame, and the session has been destroyed (i.e.
    // another user logged in), redirect (previous session) to the LTI error page
    if (user == null && isLtiHost(currentLocation) && isIFrame) {
      return action.redirect({
        location: new URL(ltiErrorLink(ltiErrorAdt.ltiSessionEnded()), defaultHost),
        cause: `Anonymous user tried to access LTI site (LTI session ended)`,
        details,
      });
    }

    // Redirect anonymous users to the default URL
    if (user == null && isLtiHost(currentLocation)) {
      return action.redirect({
        location: new URL(currentPathAndSearch, defaultHost),
        cause: 'Anonymous user tried to access LTI site',
        details,
      });
    }
  }

  return action.ok();
};
